import {
  Alert,
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  Box,
  Snackbar,
} from '@mui/material';
import OverviewIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import SettingsIcon from '@mui/icons-material/Settings';
import { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SEOHelmet from '../components/seo/SEOHelmet';
import {
  memberStatusSchema,
  groupMemberRoleSchema,
  type Meeting,
  type MeetingWrite,
} from '@penticton-dev-events/shared';

import { useAuth } from '../app/providers/AuthProvider';
import { useFirebaseServices } from '../app/providers/FirebaseProvider';
import {
  approveMembership,
  requestMembership,
  deleteGroup,
} from '../features/groups/api/groupRepository';
import GroupDetail from '../features/groups/components/GroupDetail';
import { useGroups } from '../features/groups/hooks/useGroups';
import { useMembership } from '../features/groups/hooks/useMembership';
import { useGroupMembers } from '../features/groups/hooks/useGroupMembers';
import { useGroupSettings } from '../features/groups/hooks/useGroupSettings';
import { useMeetingsByGroup } from '../features/meetings/hooks/useMeetingsByGroup';
import MeetingForm from '../features/meetings/components/MeetingForm';
import {
  createMeeting,
  updateMeetingStatus,
} from '../features/meetings/api/meetingRepository';
import Breadcrumbs from '../components/navigation/Breadcrumbs';
import { useGroupBreadcrumbs } from '../components/navigation/useBreadcrumbs';
import SecondaryNav, { SecondaryNavItem } from '../components/navigation/SecondaryNav';
import { useNotification } from '../hooks/useNotification';
import GroupMeetingsTab from '../features/groups/components/GroupMeetingsTab';
import GroupMembersTab from '../features/groups/components/GroupMembersTab';
import GroupSettingsTab from '../features/groups/components/GroupSettingsTab';
import { useAccountStatus } from '../features/auth/hooks/useAccountStatus';

const GroupDetailPage = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { firestore } = useFirebaseServices();
  const { user, profile, signIn, loading: authLoading } = useAuth();
  const { groups, loading, error } = useGroups();
  const [membershipError, setMembershipError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const { isPending, isSuspended } = useAccountStatus();
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [processingMeetingIds, setProcessingMeetingIds] = useState<Set<string>>(
    new Set(),
  );

  const group = useMemo(
    () => groups.find(item => item.id === groupId),
    [groups, groupId],
  );

  const { meetings, loading: meetingsLoading } = useMeetingsByGroup(group?.id ?? null);
  const { member: membership, loading: membershipLoading } = useMembership(
    group?.id ?? null,
    user?.uid ?? null,
    group,
  );
  const isOrganizerForGroup = Boolean(
    user && group && (user.uid === group.organizerUid || profile?.isAdmin),
  );
  const {
    members,
    filteredMembers,
    memberCount,
    loading: membersLoading,
    error: membersError,
    currentFilter: memberFilter,
    setFilter: setMemberFilter,
    approvingMemberIds,
    denyingMemberIds,
    approveMember,
    denyMember,
  } = useGroupMembers(isOrganizerForGroup ? (group?.id ?? null) : null);

  // Group settings hook
  const {
    group: settingsGroup,
    loading: settingsLoading,
    saving: settingsSaving,
    error: settingsError,
    validationErrors: settingsValidationErrors,
    hasUnsavedChanges: settingsHasUnsavedChanges,
    updateSettings,
    resetForm: resetSettingsForm,
  } = useGroupSettings(
    isOrganizerForGroup ? (group?.id ?? null) : null,
    user?.uid ?? null,
  );

  const { notification, showNotification, hideNotification } = useNotification();

  // Breadcrumbs
  const breadcrumbItems = useGroupBreadcrumbs(group?.name, group?.id);

  // Secondary navigation items
  const secondaryNavItems: SecondaryNavItem[] = useMemo(() => {
    const items: SecondaryNavItem[] = [
      {
        id: 'overview',
        label: 'Overview',
        icon: <OverviewIcon fontSize="small" />,
      },
      {
        id: 'meetings',
        label: 'Events',
        icon: <EventIcon fontSize="small" />,
        badge: meetings.length,
      },
    ];

    // Add members tab for organizers
    if (isOrganizerForGroup) {
      items.push({
        id: 'members',
        label: 'Members',
        icon: <PeopleIcon fontSize="small" />,
        badge: members.length,
      });
    }

    // Add settings tab for organizers
    if (isOrganizerForGroup) {
      items.push({
        id: 'settings',
        label: 'Settings',
        icon: <SettingsIcon fontSize="small" />,
      });
    }

    return items;
  }, [meetings.length, members.length, isOrganizerForGroup]);

  const handleJoin = async () => {
    if (!group) {
      return;
    }

    if (!user || !profile) {
      setMembershipError('Sign in is required before requesting membership.');
      void signIn();
      return;
    }

    if (isPending) {
      setMembershipError('Your account is pending approval. You cannot join groups until your account is approved.');
      return;
    }

    if (isSuspended) {
      setMembershipError('Your account has been suspended. You cannot join groups.');
      return;
    }

    if (membership?.status && membership.status !== memberStatusSchema.parse('pending')) {
      setMembershipError('You have already joined this group.');
      return;
    }

    setMembershipError(null);
    setJoining(true);

    try {
      await requestMembership(firestore, group, {
        uid: user.uid,
        role: groupMemberRoleSchema.parse('member'),
        displayName: profile.displayName,
        email: profile.email,
        photoURL: profile.photoURL || null, // Convert undefined to null
      });

      const message = group.autoAcceptMembers
        ? 'Successfully joined the group!'
        : 'Membership request submitted for approval';
      showNotification(message, 'success');
    } catch (joinError) {
      const message =
        joinError instanceof Error ? joinError.message : 'Unable to request membership';
      setMembershipError(message);
      showNotification(message, 'error');
    } finally {
      setJoining(false);
    }
  };

  const handleCreateMeeting = async (values: MeetingWrite) => {
    if (!group || !user) {
      setMembershipError('You must be signed in as the organizer to create meetings.');
      return;
    }

    try {
      await createMeeting(firestore, {
        ...values,
        groupId: group.id,
        createdBy: user.uid,
      });
      setMeetingDialogOpen(false);
      showNotification('Meeting scheduled successfully!', 'success');
    } catch (creationError) {
      const message =
        creationError instanceof Error
          ? creationError.message
          : 'Unable to schedule meeting';
      setMembershipError(message);
      showNotification(message, 'error');
    }
  };

  const handleApproveMember = async (memberId: string) => {
    try {
      await approveMember(memberId);
      showNotification('Member approved successfully!', 'success');
    } catch (approvalError) {
      const message =
        approvalError instanceof Error
          ? approvalError.message
          : 'Unable to approve member';
      showNotification(message, 'error');
    }
  };

  const handleDenyMember = async (memberId: string) => {
    try {
      await denyMember(memberId);
      showNotification('Member request denied', 'success');
    } catch (denialError) {
      const message =
        denialError instanceof Error ? denialError.message : 'Unable to deny member';
      showNotification(message, 'error');
    }
  };

  const handleDeleteGroup = async () => {
    if (!group || !isOrganizerForGroup) {
      return;
    }

    try {
      await deleteGroup(firestore, group.id);
      showNotification('Group deleted successfully', 'success');
      navigate('/groups');
    } catch (deleteError) {
      const message =
        deleteError instanceof Error ? deleteError.message : 'Unable to delete group';
      showNotification(message, 'error');
      throw deleteError; // Re-throw to keep dialog open on error
    }
  };

  const handleCancelMeeting = async (meetingId: string) => {
    if (!group || !isOrganizerForGroup) {
      return;
    }

    setProcessingMeetingIds(prev => new Set(prev).add(meetingId));

    try {
      await updateMeetingStatus(firestore, meetingId, 'canceled');
      showNotification('Meeting canceled successfully', 'success');
    } catch (cancelError) {
      const message =
        cancelError instanceof Error ? cancelError.message : 'Unable to cancel meeting';
      setMembershipError(message);
      showNotification(message, 'error');
    } finally {
      setProcessingMeetingIds(prev => {
        const next = new Set(prev);
        next.delete(meetingId);
        return next;
      });
    }
  };

  const handleRestoreMeeting = async (meetingId: string) => {
    if (!group || !isOrganizerForGroup) {
      return;
    }

    setProcessingMeetingIds(prev => new Set(prev).add(meetingId));

    try {
      await updateMeetingStatus(firestore, meetingId, 'scheduled');
      showNotification('Meeting restored successfully', 'success');
    } catch (restoreError) {
      const message =
        restoreError instanceof Error
          ? restoreError.message
          : 'Unable to restore meeting';
      setMembershipError(message);
      showNotification(message, 'error');
    } finally {
      setProcessingMeetingIds(prev => {
        const next = new Set(prev);
        next.delete(meetingId);
        return next;
      });
    }
  };

  const handleMeetingClick = (meeting: Meeting) => {
    navigate(`/meetings/${meeting.id}`);
  };

  const handleUpdateSettings = async (settings: any) => {
    try {
      await updateSettings(settings);
      showNotification('Settings updated successfully!', 'success');
    } catch (settingsError) {
      const message =
        settingsError instanceof Error
          ? settingsError.message
          : 'Unable to update settings';
      showNotification(message, 'error');
    }
  };

  if (loading) {
    return (
      <Container sx={{ py: 6 }}>
        <Typography color="text.secondary">Loading group details…</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 6 }}>
        <Alert severity="error">Failed to load groups: {error.message}</Alert>
      </Container>
    );
  }

  if (!group) {
    return (
      <Container sx={{ py: 6 }}>
        <Stack spacing={2}>
          <Typography variant="h5">Group not found</Typography>
          <Typography color="text.secondary">
            The group you are looking for does not exist or has been removed.
          </Typography>
        </Stack>
      </Container>
    );
  }

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <>
            <GroupDetail
              group={group}
              joining={joining || isPending || isSuspended}
              meetings={meetings}
              meetingsLoading={meetingsLoading}
              membership={membership}
              membershipLoading={membershipLoading || authLoading}
              onJoin={() => {
                void handleJoin();
              }}
              onMeetingClick={handleMeetingClick}
              {...(isOrganizerForGroup ? { onCancelMeeting: handleCancelMeeting } : {})}
              {...(isOrganizerForGroup ? { onRestoreMeeting: handleRestoreMeeting } : {})}
            />
          </>
        );

      case 'meetings':
        return (
          <GroupMeetingsTab
            meetings={meetings}
            loading={meetingsLoading}
            error={null}
            isOrganizer={isOrganizerForGroup}
            onCreateMeeting={() => setMeetingDialogOpen(true)}
            onMeetingClick={handleMeetingClick}
            onCancelMeeting={handleCancelMeeting}
            onRestoreMeeting={handleRestoreMeeting}
            processingMeetingIds={processingMeetingIds}
          />
        );

      case 'members':
        return (
          <GroupMembersTab
            members={members}
            filteredMembers={filteredMembers}
            memberCount={memberCount}
            loading={membersLoading}
            error={membersError}
            currentFilter={memberFilter}
            approvingMemberIds={approvingMemberIds}
            denyingMemberIds={denyingMemberIds}
            onFilterChange={setMemberFilter}
            onApproveMember={handleApproveMember}
            onDenyMember={handleDenyMember}
            isOrganizer={isOrganizerForGroup}
          />
        );

      case 'settings':
        return (
          <GroupSettingsTab
            group={group}
            loading={settingsLoading}
            saving={settingsSaving}
            error={settingsError}
            validationErrors={settingsValidationErrors}
            hasUnsavedChanges={settingsHasUnsavedChanges}
            onUpdateSettings={handleUpdateSettings}
            onResetForm={resetSettingsForm}
            onDeleteGroup={handleDeleteGroup}
            isOrganizer={isOrganizerForGroup}
          />
        );

      default:
        return null;
    }
  };

  // Generate meta tags for social sharing
  const groupUrl = `https://events.penticton.dev/groups/${group.slug}`;
  const groupTitle = group.name;
  const groupDescription = group.summary ||
    `Join ${group.name} - a developer community group in Penticton. Connect with local developers and attend tech events.`;
  const groupImage = group.posterUrl || 'https://events.penticton.dev/android-chrome-512x512.png';
  const groupKeywords = `${group.name}, penticton developers, tech community, developer group`;

  return (
    <>
      {/* SEO and Social Media Meta Tags */}
      <SEOHelmet
        title={groupTitle}
        description={groupDescription}
        image={groupImage}
        url={groupUrl}
        keywords={groupKeywords}
      />

      {/* Breadcrumbs */}
      {group && <Breadcrumbs items={breadcrumbItems} />}

      {/* Secondary Navigation */}
      {group && (
        <SecondaryNav
          items={secondaryNavItems}
          value={activeTab}
          onChange={setActiveTab}
          isAuthenticated={Boolean(user)}
          isAdmin={profile?.isAdmin ?? false}
          ariaLabel="Group navigation"
        />
      )}

      <Container sx={{ py: 6 }}>
        <Stack spacing={3}>
          {/* Tab content */}
          {renderTabContent()}

          {!user && (
            <Alert
              action={
                <Button
                  color="inherit"
                  onClick={() => {
                    setMembershipError(null);
                    void signIn();
                  }}
                >
                  Sign in
                </Button>
              }
              severity="info"
            >
              You are exploring the site as a guest. Sign in to join groups and RSVP to
              meetings.
            </Alert>
          )}
        </Stack>
      </Container>
      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={() => setMeetingDialogOpen(false)}
        open={meetingDialogOpen}
      >
        <DialogTitle>Schedule meeting</DialogTitle>
        <DialogContent>
          {group && user ? (
            <MeetingForm
              groupId={group.id}
              organizerUid={user.uid}
              onSubmit={handleCreateMeeting}
              onCancel={() => setMeetingDialogOpen(false)}
            />
          ) : (
            <Typography color="text.secondary">
              Sign in as the organizer to schedule meetings.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Feedback Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={hideNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={hideNotification}
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default GroupDetailPage;
